/**
 * Drop-in replacement for @supabase/supabase-js.
 *
 * Lovable's 125 frontend files call `supabase.from('table').select()...`. This
 * file provides the same chainable API but routes through OUR backend (no
 * Supabase npm package, no Supabase service). Requests hit the Postgrest-
 * compatible adapter at `/rest/v1/*` on backend/src/modules/v2/_pg/pg.routes.js,
 * which queries our own Postgres (testdb_xiqm / grxbooks_v2) via Sequelize.
 *
 * What it covers:
 *   - .from(t).select().eq/.neq/.gt/.gte/.lt/.lte/.like/.ilike/.is/.in/.not/.or
 *   - .order(col, {ascending}), .limit, .range, .single, .maybeSingle
 *   - .insert, .update, .upsert, .delete (with .select() to return rows)
 *   - .rpc(fn, args)
 *   - .auth.signInWithPassword / signUp / signOut / getSession / onAuthStateChange
 *   - .functions.invoke (calls /functions/v1/{name} on our backend, 501 stub for now)
 *   - .storage upload/download/remove/createSignedUrl against the backend shim
 *   - .channel(...).on(...).subscribe() — no-op (we don't have realtime)
 */

// Default to relative URL (empty string) so requests stay same-origin in
// production. Override via VITE_SUPABASE_URL only when the frontend is hosted
// on a different origin than the API (e.g. local Vite dev on :5173 hitting
// backend on :8101).
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
  ?? (import.meta.env.DEV ? 'http://localhost:8101' : '');
const STORAGE_KEY = 'grx10.session';

type SbResult<T> = { data: T | null; error: { message: string; code?: string; details?: string } | null; count?: number | null };

// ───────────────────────── Session storage ─────────────────────────
function getSession(): any | null {
  try { const s = localStorage.getItem(STORAGE_KEY); return s ? JSON.parse(s) : null; } catch { return null; }
}
function setSession(s: any | null) {
  if (s) localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  else localStorage.removeItem(STORAGE_KEY);
  emitAuthChange(s ? 'SIGNED_IN' : 'SIGNED_OUT', s);
}
function getAccessToken(): string | null { return getSession()?.access_token || null; }

const authListeners: Array<(event: string, session: any) => void> = [];
function emitAuthChange(event: string, session: any) {
  for (const fn of authListeners) try { fn(event, session); } catch (e) { console.warn(e); }
}

// ───────────────────────── Low-level fetch ─────────────────────────
async function pgFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || 'grx10-public-anon-key',
    ...(init.headers as Record<string, string> || {}),
  };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(`${SUPABASE_URL}${path}`, { ...init, headers });
}

// ───────────────────────── Query builder ─────────────────────────
type FilterTuple = [string, string, any];

class QueryBuilder<T = any> implements PromiseLike<SbResult<T>> {
  private _table: string;
  private _action: 'select' | 'insert' | 'update' | 'delete' | 'upsert' = 'select';
  private _select = '*';
  private _filters: FilterTuple[] = [];
  private _order: Array<{ col: string; ascending: boolean; nullsFirst?: boolean }> = [];
  private _limit: number | null = null;
  private _offset: number | null = null;
  private _single: 'one' | 'maybe' | null = null;
  private _body: any = null;
  private _returning = false;
  private _upsertOpts: { onConflict?: string } | null = null;
  // Mirror supabase-js v2: .select(cols, { count: 'exact' | 'planned' | 'estimated' })
  // sends Prefer: count=<mode> and parses Content-Range back into _exec()'s result.
  // Without this, every paginated screen reading `count` from the response got
  // undefined → footer "0 of 0" and TablePagination hid itself.
  private _count: 'exact' | 'planned' | 'estimated' | null = null;

  constructor(table: string) { this._table = table; }

  select(cols = '*', opts?: { count?: 'exact' | 'planned' | 'estimated' }) {
    this._select = cols;
    if (opts?.count) this._count = opts.count;
    if (this._action === 'insert' || this._action === 'update' || this._action === 'upsert' || this._action === 'delete') this._returning = true;
    return this;
  }

  eq(col: string, val: any) { this._filters.push([col, 'eq', val]); return this; }
  neq(col: string, val: any) { this._filters.push([col, 'neq', val]); return this; }
  gt(col: string, val: any) { this._filters.push([col, 'gt', val]); return this; }
  gte(col: string, val: any) { this._filters.push([col, 'gte', val]); return this; }
  lt(col: string, val: any) { this._filters.push([col, 'lt', val]); return this; }
  lte(col: string, val: any) { this._filters.push([col, 'lte', val]); return this; }
  like(col: string, pat: string) { this._filters.push([col, 'like', pat]); return this; }
  ilike(col: string, pat: string) { this._filters.push([col, 'ilike', pat]); return this; }
  is(col: string, val: null | boolean) { this._filters.push([col, 'is', val]); return this; }
  in(col: string, vals: any[]) { this._filters.push([col, 'in', vals]); return this; }
  contains(col: string, val: any) { this._filters.push([col, 'cs', val]); return this; }
  containedBy(col: string, val: any) { this._filters.push([col, 'cd', val]); return this; }
  not(col: string, op: string, val: any) { this._filters.push([col, `not.${op}`, val]); return this; }
  or(filter: string) { this._filters.push(['__or__', 'or', filter]); return this; }
  match(obj: Record<string, any>) { for (const [k, v] of Object.entries(obj)) this.eq(k, v); return this; }

  order(col: string, opts?: { ascending?: boolean; nullsFirst?: boolean }) {
    this._order.push({ col, ascending: opts?.ascending !== false, nullsFirst: opts?.nullsFirst });
    return this;
  }
  limit(n: number) { this._limit = n; return this; }
  range(from: number, to: number) { this._offset = from; this._limit = to - from + 1; return this; }
  single() { this._single = 'one'; return this; }
  maybeSingle() { this._single = 'maybe'; return this; }

  insert(rows: any | any[]) { this._action = 'insert'; this._body = rows; return this; }
  update(patch: any) { this._action = 'update'; this._body = patch; return this; }
  upsert(rows: any | any[], opts?: { onConflict?: string }) { this._action = 'upsert'; this._body = rows; this._upsertOpts = opts ?? null; return this; }
  delete() { this._action = 'delete'; return this; }

  // ───── Query string builder (Postgrest convention) ─────
  private _qs(): string {
    const params = new URLSearchParams();
    if (this._action === 'select') params.set('select', this._select);
    for (const [col, op, val] of this._filters) {
      if (col === '__or__' && op === 'or') { params.append('or', `(${val})`); continue; }
      if (op === 'in') params.append(col, `in.(${(val as any[]).map((v) => String(v)).join(',')})`);
      else if (op === 'is') params.append(col, `is.${val === null ? 'null' : val}`);
      else params.append(col, `${op}.${val}`);
    }
    if (this._order.length) {
      params.set('order', this._order.map((o) => `${o.col}.${o.ascending ? 'asc' : 'desc'}${o.nullsFirst ? '.nullsfirst' : ''}`).join(','));
    }
    if (this._limit != null) params.set('limit', String(this._limit));
    if (this._offset != null) params.set('offset', String(this._offset));
    if (this._upsertOpts?.onConflict) params.set('on_conflict', this._upsertOpts.onConflict);
    const s = params.toString();
    return s ? `?${s}` : '';
  }

  private async _exec(): Promise<SbResult<T>> {
    const path = `/rest/v1/${this._table}${this._qs()}`;
    const headers: Record<string, string> = {};
    if (this._single === 'one') headers.Accept = 'application/vnd.pgrst.object+json';
    if (this._action !== 'select' && (this._returning || this._single)) headers.Prefer = 'return=representation';
    if (this._upsertOpts) headers.Prefer = (headers.Prefer ? headers.Prefer + ',' : '') + 'resolution=merge-duplicates';
    if (this._count) headers.Prefer = (headers.Prefer ? headers.Prefer + ',' : '') + `count=${this._count}`;

    const init: RequestInit = { headers };
    if (this._action === 'select') init.method = 'GET';
    else if (this._action === 'delete') init.method = 'DELETE';
    else if (this._action === 'update') { init.method = 'PATCH'; init.body = JSON.stringify(this._body); }
    else { init.method = 'POST'; init.body = JSON.stringify(this._body); }

    try {
      const resp = await pgFetch(path, init);
      if (!resp.ok) {
        let detail: any = null;
        try { detail = await resp.json(); } catch { detail = await resp.text(); }
        return { data: null, error: { message: detail?.message || detail?.detail || resp.statusText, code: detail?.code, details: typeof detail === 'string' ? detail : JSON.stringify(detail) } };
      }
      // Parse Content-Range total (last segment after '/') into the count field
      // so callers using { count: 'exact' } see the same shape as supabase-js v2.
      let count: number | null = null;
      if (this._count) {
        const cr = resp.headers.get('content-range');
        if (cr) {
          const total = cr.split('/').pop();
          if (total && total !== '*') {
            const n = Number(total);
            if (!Number.isNaN(n)) count = n;
          }
        }
      }
      if (resp.status === 204) return { data: null as any, error: null, count };
      const json = await resp.json();
      if (this._single === 'maybe' && Array.isArray(json) && json.length === 0) return { data: null as any, error: null, count };
      if (this._single === 'maybe' && Array.isArray(json) && json.length > 0) return { data: json[0], error: null, count };
      return { data: json, error: null, count };
    } catch (e: any) {
      return { data: null, error: { message: e.message || String(e) } };
    }
  }

  then<TResult1 = SbResult<T>, TResult2 = never>(
    onFulfilled?: ((value: SbResult<T>) => TResult1 | PromiseLike<TResult1>) | null,
    onRejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null,
  ): PromiseLike<TResult1 | TResult2> {
    return this._exec().then(onFulfilled, onRejected);
  }
}

// ───────────────────────── Auth surface ─────────────────────────
const auth = {
  async signInWithPassword({ email, password }: { email: string; password: string }): Promise<SbResult<any>> {
    try {
      const resp = await pgFetch('/auth/v1/token?grant_type=password', { method: 'POST', body: JSON.stringify({ email, password }) });
      const json = await resp.json();
      if (!resp.ok) return { data: null, error: { message: json.error_description || json.error || resp.statusText } };
      setSession(json);
      return { data: { user: json.user, session: json }, error: null };
    } catch (e: any) { return { data: null, error: { message: e.message } }; }
  },
  async signUp({ email, password }: { email: string; password: string }): Promise<SbResult<any>> {
    return { data: null, error: { message: 'signup_disabled — provisioned by admin' } };
  },
  async signOut(): Promise<{ error: any }> { setSession(null); return { error: null }; },
  async getSession(): Promise<SbResult<{ session: any }>> {
    const s = getSession();
    return { data: { session: s }, error: null };
  },
  async getUser(): Promise<SbResult<{ user: any }>> {
    const s = getSession();
    return { data: { user: s?.user ?? null }, error: null };
  },
  onAuthStateChange(cb: (event: string, session: any) => void) {
    authListeners.push(cb);
    // Fire initial state
    queueMicrotask(() => cb(getSession() ? 'INITIAL_SESSION' : 'INITIAL_SESSION', getSession()));
    return { data: { subscription: { unsubscribe: () => { const i = authListeners.indexOf(cb); if (i >= 0) authListeners.splice(i, 1); } } } };
  },
  async resetPasswordForEmail() { return { data: null, error: { message: 'not_implemented' } }; },
  async updateUser() { return { data: null, error: { message: 'not_implemented' } }; },
  async refreshSession() { return { data: { session: getSession() }, error: null }; },
  async setSession({ access_token, refresh_token }: { access_token: string; refresh_token: string }): Promise<SbResult<any>> {
    // Persist a session given tokens (e.g. after MS365 OAuth callback).
    // We decode the JWT to extract the user claims.
    try {
      const [, payload] = access_token.split('.');
      const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      const session = {
        access_token,
        refresh_token,
        token_type: 'bearer',
        expires_in: claims.exp ? claims.exp - Math.floor(Date.now() / 1000) : 8 * 3600,
        expires_at: claims.exp,
        user: {
          id: claims.sub,
          email: claims.email,
          app_metadata: { is_platform_admin: claims.is_platform_admin, organization_id: claims.org_id },
        },
      };
      setSession(session);
      return { data: { session, user: session.user }, error: null };
    } catch (e: any) {
      return { data: null, error: { message: e.message } };
    }
  },
};

// ───────────────────────── RPC ─────────────────────────
async function rpc<T = any>(fn: string, args: any = {}): Promise<SbResult<T>> {
  try {
    const resp = await pgFetch(`/rest/v1/rpc/${fn}`, { method: 'POST', body: JSON.stringify(args) });
    const json = await resp.json().catch(() => null);
    if (!resp.ok) return { data: null, error: { message: json?.message || json?.detail || resp.statusText, code: json?.code } };
    return { data: json, error: null };
  } catch (e: any) { return { data: null, error: { message: e.message } }; }
}

// ───────────────────────── Edge functions ─────────────────────────
const functions = {
  async invoke<T = any>(name: string, opts: { body?: any; headers?: Record<string, string> } = {}): Promise<SbResult<T>> {
    try {
      const resp = await pgFetch(`/functions/v1/${name}`, { method: 'POST', body: JSON.stringify(opts.body ?? {}), headers: opts.headers });
      const json = await resp.json().catch(() => null);
      if (!resp.ok) return { data: null, error: { message: json?.error || resp.statusText } };
      return { data: json, error: null };
    } catch (e: any) { return { data: null, error: { message: e.message } }; }
  },
};

// ───────────────────────── Storage (local-disk shim on backend) ─────────────────────────
/** Percent-encodes each segment so `#` or `?` in a name cannot truncate a URL. */
function encodeStoragePath(filePath: string) {
  return filePath.split('/').map(encodeURIComponent).join('/');
}

const storage = {
  from(bucket: string) {
    // `options.download` asks for a Content-Disposition attachment, so the
    // caller gets a "save as" instead of the file rendering in a tab.
    const createSignedUrl = async (
      filePath: string,
      expiresIn: number,
      options?: { download?: string | boolean },
    ): Promise<SbResult<{ signedUrl: string }>> => {
      try {
        const resp = await pgFetch(`/storage/v1/object/sign/${bucket}/${encodeStoragePath(filePath)}`, {
          method: 'POST',
          body: JSON.stringify({ expiresIn, download: options?.download ?? false }),
        });
        const json = await resp.json().catch(() => null);
        // Without this the caller received the string "undefined" as a URL
        // and navigated to it. A failure must read as a failure.
        if (!resp.ok || !json?.signedURL) {
          return { data: null, error: { message: json?.error || `sign_failed_${resp.status}` } };
        }
        // S3 mode returns an absolute URL; local mode returns a rooted path.
        const signedUrl = /^https?:\/\//i.test(json.signedURL)
          ? json.signedURL
          : `${SUPABASE_URL}${json.signedURL}`;
        return { data: { signedUrl }, error: null };
      } catch (e: any) { return { data: null, error: { message: e.message } }; }
    };

    return {
      async upload(filePath: string, file: File | Blob | ArrayBuffer): Promise<SbResult<any>> {
        try {
          const body = file instanceof Blob ? await file.arrayBuffer() : file;
          const resp = await fetch(`${SUPABASE_URL}/storage/v1/object/${bucket}/${encodeStoragePath(filePath)}`, {
            method: 'POST',
            headers: { 'Content-Type': (file as any)?.type || 'application/octet-stream', Authorization: `Bearer ${getAccessToken() || ''}` },
            body: body as any,
          });
          if (!resp.ok) return { data: null, error: { message: await resp.text() } };
          return { data: await resp.json(), error: null };
        } catch (e: any) { return { data: null, error: { message: e.message } }; }
      },
      async download(filePath: string): Promise<SbResult<Blob>> {
        try {
          // Private buckets require a signed token on the read route, so mint
          // one rather than fetching the object path directly.
          const { data, error } = await createSignedUrl(filePath, 300);
          if (error || !data?.signedUrl) return { data: null, error: error ?? { message: 'not_found' } };
          const resp = await fetch(data.signedUrl);
          if (!resp.ok) return { data: null, error: { message: `download_failed_${resp.status}` } };
          return { data: await resp.blob(), error: null };
        } catch (e: any) { return { data: null, error: { message: e.message } }; }
      },
      async remove(paths: string[]): Promise<SbResult<any>> {
        try {
          const results = await Promise.all(paths.map((p) =>
            fetch(`${SUPABASE_URL}/storage/v1/object/${bucket}/${encodeStoragePath(p)}`, {
              method: 'DELETE', headers: { Authorization: `Bearer ${getAccessToken() || ''}` },
            })));
          // A refused delete used to look like a success, so callers went on to
          // drop the database row and orphan the file.
          const failed = results.find((r) => !r.ok);
          if (failed) return { data: null, error: { message: `delete_failed_${failed.status}` } };
          return { data: { count: paths.length }, error: null };
        } catch (e: any) { return { data: null, error: { message: e.message } }; }
      },
      // Only meaningful for buckets the backend serves without a token
      // (tenant-branding). Anything else needs createSignedUrl.
      getPublicUrl(path: string) { return { data: { publicUrl: `${SUPABASE_URL}/storage/v1/object/public/${bucket}/${encodeStoragePath(path)}` } }; },
      createSignedUrl,
      async list() { return { data: [], error: null }; },
    };
  },
};

// ───────────────────────── Realtime (no-op) ─────────────────────────
function channel(_name: string) {
  const chan = {
    on(_evt: any, _filter: any, _cb: any) { return chan; },
    subscribe(_cb?: any) { return chan; },
    unsubscribe() { return chan; },
  };
  return chan;
}

// ───────────────────────── Export ─────────────────────────
export const supabase = {
  from: <T = any>(table: string) => new QueryBuilder<T>(table),
  rpc,
  auth,
  functions,
  storage,
  channel,
  removeChannel: () => {},
  getChannels: () => [],
} as any;

export default supabase;
